import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  featureFlags,
  type FeatureFlagCatalogRow,
  type CatalogCreate,
} from '@/api/featureFlags';
import { resellers } from '@/api/resellers';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Lock, Plus, Save, Loader2, ArrowRight, Info, Pencil } from 'lucide-react';
import { FEATURE_FLAGS_QUERY_KEY } from '@/hooks/useFeatureFlag';
import { formatRef } from '@/lib/formatRef';

export function FeaturesOwner() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['feature-flags', 'catalog'],
    queryFn: () => featureFlags.catalog(),
  });
  const { data: resellersData } = useQuery({
    queryKey: ['resellers'],
    queryFn: () => resellers.list(),
  });

  const groups = groupByCategory(data?.rows ?? []);

  return (
    <>
      <PageHeader
        eyebrow="Platform settings"
        title="Feature flags"
        description="The catalog of features that exist across Ivrello. Pricing is set per reseller on each reseller's settings page."
      />
      <div className="flex items-center justify-end mb-4">
        <AddFeatureDialog />
      </div>

      <Card className="mb-4 border-l-4 border-l-blue-400 bg-blue-50/40">
        <CardContent className="py-3.5 flex items-start gap-3">
          <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900 space-y-1.5">
            <p>
              This page is the <span className="font-semibold">system-wide catalog</span>:
              which features exist, their default state, and whether they're core.
            </p>
            <p className="text-blue-800/80">
              To set what you charge a specific reseller, open that reseller's
              settings from the <Link to="/resellers" className="font-medium underline underline-offset-2">Resellers list</Link>.
            </p>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card><CardContent className="py-10 text-sm text-muted-foreground">Loading…</CardContent></Card>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <Card key={g.category}>
              <CardHeader>
                <CardTitle className="text-base">{g.category}</CardTitle>
                <CardDescription>
                  {g.category === 'Core'
                    ? "Core features can't be disabled — they're fundamental to Ivrello."
                    : 'Toggle the default-on state. Pricing is configured per reseller.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y divide-border">
                  {g.rows.map((row) => (
                    <FeatureRow
                      key={row.key}
                      row={row}
                      onSaved={() => {
                        qc.invalidateQueries({ queryKey: ['feature-flags', 'catalog'] });
                        qc.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEY });
                      }}
                    />
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}

          {resellersData?.rows.length ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Set pricing per reseller</CardTitle>
                <CardDescription>
                  Jump to a reseller's settings to enable features and set what you charge them.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y divide-border">
                  {resellersData.rows.slice(0, 6).map((r) => (
                    <li key={r.id} className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs text-muted-foreground tabular-nums w-8">
                          #{formatRef(r.reseller_number)}
                        </span>
                        <span className="text-sm font-medium truncate">{r.name}</span>
                      </div>
                      <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                        <Link to={`/resellers/${r.id}/settings/features`}>
                          Features <ArrowRight className="h-3 w-3 ml-1" />
                        </Link>
                      </Button>
                    </li>
                  ))}
                </ul>
                {resellersData.rows.length > 6 && (
                  <div className="p-3 border-t text-xs text-muted-foreground text-center">
                    <Link to="/resellers" className="hover:underline">View all {resellersData.rows.length} resellers →</Link>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}
    </>
  );
}

interface FeatureRowProps {
  row: FeatureFlagCatalogRow;
  onSaved: () => void;
}

function FeatureRow({ row, onSaved }: FeatureRowProps) {
  const [editing, setEditing] = useState(false);
  const [defaultEnabled, setDefaultEnabled] = useState(row.default_enabled);
  const [displayName, setDisplayName] = useState(row.display_name);
  const [category, setCategory] = useState(row.category);
  const [description, setDescription] = useState(row.description ?? '');
  const [core, setCore] = useState(row.core);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => featureFlags.updateCatalog(row.key, {
      display_name: displayName,
      category,
      description: description.trim() || null,
      default_enabled: defaultEnabled,
      core,
    }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onSaved();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed.'),
  });

  // Switching the toggle saves immediately (no edit dialog needed).
  const quickToggle = useMutation({
    mutationFn: (next: boolean) =>
      featureFlags.updateCatalog(row.key, { default_enabled: next }),
    onSuccess: () => { setError(null); onSaved(); },
    onError: (e, next) => {
      setDefaultEnabled(!next);
      setError(e instanceof Error ? e.message : 'Save failed.');
    },
  });

  function resetForm() {
    setDisplayName(row.display_name);
    setCategory(row.category);
    setDescription(row.description ?? '');
    setCore(row.core);
    setDefaultEnabled(row.default_enabled);
  }

  return (
    <li className="grid grid-cols-[1fr_auto] gap-4 p-4 items-start">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium">{row.display_name}</p>
          <code className="text-[10px] text-muted-foreground font-mono">{row.key}</code>
          {row.core && (
            <Badge variant="outline" className="text-[10px] gap-1 border-amber-300 text-amber-700">
              <Lock className="h-2.5 w-2.5" /> Core
            </Badge>
          )}
        </div>
        {row.description && (
          <p className="text-xs text-muted-foreground mt-1">{row.description}</p>
        )}
        {error && <p role="alert" className="text-xs text-destructive mt-2">{error}</p>}
      </div>
      <div className="flex flex-col items-end gap-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Default on</span>
          <Switch
            checked={defaultEnabled}
            disabled={row.core || quickToggle.isPending}
            onCheckedChange={(v) => {
              setDefaultEnabled(v);
              quickToggle.mutate(v);
            }}
          />
        </label>
        <Dialog
          open={editing}
          onOpenChange={(v) => {
            if (!v) resetForm();
            setEditing(v);
          }}
        >
          <DialogTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 text-xs">
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit feature</DialogTitle>
              <DialogDescription>
                Change the display name, category, description, or core-flag.
                Pricing is set per reseller — not here.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label>Display name</Label>
                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Category</Label>
                <Input value={category} onChange={(e) => setCategory(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={defaultEnabled} onCheckedChange={setDefaultEnabled} disabled={core} />
                  Default on
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={core}
                    onCheckedChange={(v) => {
                      setCore(v);
                      if (v) setDefaultEnabled(true);
                    }}
                  />
                  Core (always on)
                </label>
              </div>
              {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { resetForm(); setEditing(false); }}>Cancel</Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </li>
  );
}

function groupByCategory(rows: FeatureFlagCatalogRow[]) {
  const order: Record<string, number> = { Core: 0, Communication: 1, Engagement: 2, Commerce: 3 };
  const map = new Map<string, FeatureFlagCatalogRow[]>();
  for (const r of rows) {
    if (!map.has(r.category)) map.set(r.category, []);
    map.get(r.category)!.push(r);
  }
  return Array.from(map.entries())
    .map(([category, rows]) => ({ category, rows: [...rows].sort((a, b) => a.sort_order - b.sort_order) }))
    .sort((a, b) => (order[a.category] ?? 99) - (order[b.category] ?? 99));
}

function AddFeatureDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CatalogCreate>({
    key: '', display_name: '', category: 'General',
    description: '', default_enabled: false, core: false,
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (b: CatalogCreate) => featureFlags.createCatalog(b),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feature-flags', 'catalog'] });
      qc.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEY });
      setOpen(false);
      setForm({ key: '', display_name: '', category: 'General', description: '', default_enabled: false, core: false });
      setErr(null);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Create failed'),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="h-3.5 w-3.5 mr-1" />Add feature</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a new feature flag</DialogTitle>
          <DialogDescription>
            Create a new toggleable feature. Use a lowercase snake_case key — it's referenced in code.
            Per-reseller pricing is set later, from each reseller's settings page.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Key</Label>
            <Input
              value={form.key}
              onChange={(e) => setForm({ ...form, key: e.target.value })}
              placeholder="my_new_feature"
              className="font-mono"
            />
          </div>
          <div className="space-y-1">
            <Label>Display name</Label>
            <Input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="My New Feature"
            />
          </div>
          <div className="space-y-1">
            <Label>Category</Label>
            <Input
              value={form.category ?? ''}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Communication / Commerce / Engagement"
            />
          </div>
          <div className="space-y-1">
            <Label>Description</Label>
            <Input
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What does this feature do?"
            />
          </div>
          <div className="flex items-end gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={form.default_enabled ?? false}
                onCheckedChange={(v) => setForm({ ...form, default_enabled: v })}
              />
              Default on
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={form.core ?? false}
                onCheckedChange={(v) =>
                  setForm({ ...form, core: v, default_enabled: v ? true : (form.default_enabled ?? false) })
                }
              />
              Core (always on)
            </label>
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => create.mutate(form)}
            disabled={!form.key.trim() || !form.display_name.trim() || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
